import { arktypeResolver } from '@hookform/resolvers/arktype';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';

import { DatePicker } from '@/components/atoms/DatePicker';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { TimezoneContext } from '@/hooks/useDateFormatter';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { CustomerFiltersFormData, customerFiltersSchema } from '@/schemas/customerFiltersSchema';
import { BeneficiaryType } from '@/types/customer';
import { VerificationResult, VerificationStatus } from '@/types/customerKYXVerification';
import { GetCustomerKYXVerificationsFilters } from '@/types/getCustomerKYXVerificationsFilters';

const STATUS_OPTIONS: readonly VerificationStatus[] = [
  VerificationStatus.NOT_STARTED,
  VerificationStatus.SUBMITTED,
  VerificationStatus.RUNNING,
  VerificationStatus.PENDING,
  VerificationStatus.FAILED,
  VerificationStatus.COMPLETED,
];

const RESULT_OPTIONS: readonly VerificationResult[] = [
  VerificationResult.APPROVED,
  VerificationResult.DECLINED,
  VerificationResult.REVIEW,
  VerificationResult.UNKNOWN,
];

interface Props {
  readonly filters: GetCustomerKYXVerificationsFilters;
  onChange(filter: GetCustomerKYXVerificationsFilters): void;
}

export const CustomersFilters: React.FC<Props> = (props: Props): React.ReactElement => {
  const { filters, onChange } = props;
  const tr = useTranslator();
  const timezone = React.useContext(TimezoneContext);
  const fromDateResetRef = React.useRef<VoidFunction>((): void => {});
  const toDateResetRef = React.useRef<VoidFunction>((): void => {});

  const { control, handleSubmit, reset, formState, watch, trigger } =
    useFormWithErrors<CustomerFiltersFormData>({
      defaultValues: {
        type: filters.type ?? 'ALL',
        verificationStatus: filters.verificationStatus ?? 'ALL',
        verificationResult: filters.verificationResult ?? 'ALL',
        fromDate: filters.fromDate ?? '',
        toDate: filters.toDate ?? '',
      },
      resolver: arktypeResolver(customerFiltersSchema),
      mode: 'onChange',
    });
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  const { isDirty } = formState;

  const onSubmit = React.useCallback(
    (formData: CustomerFiltersFormData): void => {
      onChange({
        ...filters,
        type: formData.type === 'ALL' ? undefined : formData.type,
        verificationStatus:
          formData.verificationStatus === 'ALL' ? undefined : formData.verificationStatus,
        verificationResult:
          formData.verificationResult === 'ALL' ? undefined : formData.verificationResult,
        fromDate: formData.fromDate ?? undefined,
        toDate: formData.toDate ?? undefined,
      });
    },
    [onChange, filters],
  );

  const handleReset = React.useCallback((): void => {
    fromDateResetRef.current();
    toDateResetRef.current();

    reset(DEFAULT_FORM_VALUES);

    onChange({
      ...filters,
      type: undefined,
      verificationStatus: undefined,
      verificationResult: undefined,
      fromDate: undefined,
      toDate: undefined,
    });
  }, [filters, onChange, reset]);

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={styles.container}>
        <ControllerWithError
          name="fromDate"
          label={tr('FROM_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<CustomerFiltersFormData>): React.ReactElement => (
            <DatePicker
              {...field}
              label={tr('FROM_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              value={field.value ? parseISO(field.value) : null}
              onChange={(date: Date | null): void => {
                if (!date) {
                  field.onChange('');
                } else {
                  const adjustedDate: Date = startOfDay(date);
                  const isoString: string = formatInTimeZone(
                    adjustedDate,
                    timezone,
                    DATE_PARSED_FORMAT,
                  );
                  field.onChange(isoString);
                }
                trigger('toDate');
              }}
              format={DATE_DISPLAY_FORMAT}
              reset={fromDateResetRef}
              shouldDisableDate={(date: Date): boolean => isAfter(date, parseISO(toDate))}
            />
          )}
        />
        <ControllerWithError
          name="toDate"
          label={tr('TO_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<CustomerFiltersFormData>): React.ReactElement => (
            <DatePicker
              {...field}
              label={tr('TO_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              value={field.value ? parseISO(field.value) : null}
              onChange={(date: Date | null): void => {
                if (!date) {
                  field.onChange('');
                } else {
                  const adjustedDate: Date = endOfDay(date);
                  const isoString: string = formatInTimeZone(
                    adjustedDate,
                    timezone,
                    DATE_PARSED_FORMAT,
                  );
                  field.onChange(isoString);
                }
              }}
              format={DATE_DISPLAY_FORMAT}
              reset={toDateResetRef}
              disableFuture
              shouldDisableDate={(date: Date): boolean => isBefore(date, parseISO(fromDate))}
            />
          )}
        />
        <ControllerWithError
          name="verificationStatus"
          label={tr('STATUS')}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<CustomerFiltersFormData>): React.ReactElement => (
            <TextField {...field} select fullWidth>
              <MenuItem value="ALL">{tr('ALL')}</MenuItem>
              {STATUS_OPTIONS.map(
                (status: VerificationStatus): React.ReactElement => (
                  <MenuItem key={status} value={status}>
                    {tr(status)}
                  </MenuItem>
                ),
              )}
            </TextField>
          )}
        />
        <ControllerWithError
          name="verificationResult"
          label={tr('RESULT')}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<CustomerFiltersFormData>): React.ReactElement => (
            <TextField {...field} select fullWidth>
              <MenuItem value="ALL">{tr('ALL')}</MenuItem>
              {RESULT_OPTIONS.map(
                (result: VerificationResult): React.ReactElement => (
                  <MenuItem key={result} value={result}>
                    {tr(result)}
                  </MenuItem>
                ),
              )}
            </TextField>
          )}
        />
        <ControllerWithError
          name="type"
          label={tr('TYPE')}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<CustomerFiltersFormData>): React.ReactElement => (
            <TextField {...field} select fullWidth>
              <MenuItem value="ALL">{tr('ALL')}</MenuItem>
              <MenuItem value={BeneficiaryType.individual}>{tr('INDIVIDUAL')}</MenuItem>
              <MenuItem value={BeneficiaryType.business}>{tr('BUSINESS')}</MenuItem>
            </TextField>
          )}
        />
      </Box>
      <Stack alignItems="center" mt={6} gap={3}>
        <Button type="submit" variant="contained" fullWidth disabled={!isDirty}>
          {tr('APPLY')}
        </Button>
        <Button variant="text" sx={styles.textButton} disableRipple onClick={handleReset}>
          {tr('RESET')}
        </Button>
      </Stack>
    </Stack>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: '1fr 1fr',
  },
  textButton: {
    color: 'text.primary',
  },
};

const DEFAULT_FORM_VALUES: CustomerFiltersFormData = {
  type: 'ALL',
  verificationStatus: 'ALL',
  verificationResult: 'ALL',
  fromDate: '',
  toDate: '',
};
const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';
const DATE_PARSED_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
